import { UIBridge } from "@/game/libs/ui-bridge";
import { Engine } from "@/game/libs/engine/engine";
import { AssetPack } from "@/game/libs/engine/loader/asset-loader";
import { Container, Texture, Sprite, TilingSprite } from "pixi.js";
import PlayerControlSystem from "./ecs/systems/PlayerControlSystem";
import CameraSystem from "./ecs/systems/CameraSystem";
import ObstacleSystem from "./ecs/systems/ObstacleSystem";
import ObstacleCreationSystem from "./ecs/systems/ObstacleCreationSystem";
import YetiSystem from "./ecs/systems/YetiSystem";
import ScoreSystem from "./ecs/systems/ScoreSystem";
import Camera from "./ecs/components/camera";
import Background from "./ecs/components/background";
import {
  createPlayer,
  createTopTenButton,
  createHelpButton,
  createHeart,
  createText,
  createObstacleContainer,
  createYeti,
  createTunein,
  createPlayButton,
  createFlag,
  createLogo
} from "./ski-factory";
import { getMedal } from "@/game/skifree/difficulty";
import { getLaunchState, LaunchState } from "@/game/skifree/util";

function clampSize(
  width: number,
  height: number,
  tw: number,
  th: number
): { width: number; height: number; scale: number } {
  const scaleW = width / tw;
  const scaleH = height / th;
  const scaleBy = Math.min(scaleW, scaleH);

  return {
    width: tw * scaleBy,
    height: th * scaleBy,
    scale: scaleBy
  };
}
export default class SkiGame extends Engine {
  /** The UI bridge to the outer vue application */
  protected bridge: UIBridge;
  private _parentResize: () => void;

  constructor(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    assetPack: AssetPack,
    bridge: UIBridge,
    parentResize: () => void
  ) {
    super(canvas, SkiGame.GAME_WIDTH, SkiGame.GAME_HEIGHT, assetPack);
    this.resize(width, height);
    this.bridge = bridge;
    var bgTrack = this.assetpack.sound.track as Howl;
    bgTrack.volume(0.75);
    bgTrack.loop(true);
    bgTrack.play();
    this._parentResize = parentResize;
    this.createGame();
  }

  createGame(): void {
    this._parentResize();
    this._container = new Container();
    this._top = new Container();
    this._bottom = new Container();
    Engine.world.registerSystem(ObstacleCreationSystem);
    Engine.world.registerSystem(PlayerControlSystem);
    Engine.world.registerSystem(YetiSystem);
    Engine.world.registerSystem(ObstacleSystem);
    Engine.world.registerSystem(CameraSystem);
    Engine.world.registerSystem(ScoreSystem);

    var bg = this.assetpack.texture.EOTP_snow_tile as Texture;
    var tilingSprite = new TilingSprite(
      bg,
      Engine.stage.width,
      Engine.stage.height
    );
    Engine.container.addChild(tilingSprite);
    Engine.world
      .createEntity()
      .addComponent(Background, { sprite: tilingSprite });

    Engine.container.addChild(this._container);
    this._container.addChild(this._bottom);
    this._container.addChild(this._top);

    var houseSprite = new Sprite(this.assetpack.texture.EOTP_lodge);
    houseSprite.x = -120;
    houseSprite.y = -150;

    var logoSprite = createLogo(this.assetpack, -7, 115);

    this._bottom.addChild(houseSprite);
    this._bottom.addChild(createFlag(this.assetpack, -38, 5));
    this._bottom.addChild(createFlag(this.assetpack, 52, 5));
    this._bottom.addChild(logoSprite);
    this._bottom.addChild(createTunein(0, 193, this.tuneInMessage()));

    this._bottom.addChild(createPlayButton(this.assetpack, 0, 225));

    var obsSys: ObstacleCreationSystem = Engine.world.getSystem(
      ObstacleCreationSystem
    ) as ObstacleCreationSystem;
    obsSys.assetPack = this.assetpack;
    obsSys.over = this._top;
    obsSys.under = this._bottom;

    var shadow = new Sprite(this.assetpack.texture.EOTP_skier_shadow_idle);
    shadow.anchor.x = 0.5;
    shadow.anchor.y = 0.5;
    shadow.alpha = 0.7;
    this._bottom.addChild(shadow);
    this._top.addChild(
      createPlayer(this.assetpack, shadow, (points: number) => {
        Engine.world.stop();
        this.bridge.state.distance = points;
        this.bridge.state.score = points;
        this.bridge.state.medal = getMedal(points);
        this.bridge.gameOver();
      })
    );

    createObstacleContainer(this._top);
    this._bottom.addChild(createYeti(this.assetpack, 0, -1000));
    Engine.world.createEntity().addComponent(Camera, {
      stage: this._container,
      width: Engine.stage.width,
      height: Engine.stage.height
    });
    Engine.container.addChild(this.createHud());

    Engine.world.execute(0, 0);
    Engine.world.stop();
  }

  createHud(): Container {
    var container = new Container();
    var heartFull: Texture = this.assetpack.texture
      .EOTP_HUD_fullheart as Texture;
    var heartBroken: Texture = this.assetpack.texture
      .EOTP_HUD_brokenheart as Texture;
    var help: Texture = this.assetpack.texture.EOTP_HUD_help as Texture;
    var leaderboard: Texture = this.assetpack.texture
      .EOTP_HUD_leaderboard as Texture;

    container.addChild(createText(125, 8, "Score: 0"));
    container.addChild(createHeart(155, 20, 2, heartFull, heartBroken));
    container.addChild(createHeart(172, 20, 1, heartFull, heartBroken));
    container.addChild(createHeart(190, 20, 0, heartFull, heartBroken));
    container.addChild(
      createHelpButton(8, 10, this.bridge.instructionsClicked, help)
    );
    container.addChild(
      createTopTenButton(185, 38, this.bridge.leaderboardClicked, leaderboard)
    );
    return container;
  }

  tuneInMessage(): Texture | undefined {
    switch (getLaunchState()) {
      case LaunchState.BEFORE:
        return this.assetpack.texture.Thurs_Dec_5_at_9_8c;
      case LaunchState.CURRENT:
        return this.assetpack.texture.TONIGHT_at_9_8c;
      case LaunchState.AFTER:
        return this.assetpack.texture.Thursdays_at_9_8c;
    }
    return undefined;
  }

  /**
   * Pauses the gameplay
   * @param shouldPause If the gameplay should be paused pass true
   */
  public pause(shouldPause: boolean) {
    if (shouldPause) {
      Engine.world.stop();
    } else {
      Engine.world.play();
    }
  }

  public resize(width: number, height: number) {
    // find the target width and height:
    const size = clampSize(
      width,
      height,
      SkiGame.GAME_WIDTH,
      SkiGame.GAME_HEIGHT
    );
    Engine.container.scale.set(size.scale);
    super.resize(size.width, size.height);
  }

  /**
   * Restarts gameplay
   */
  public restart() {
    Engine.reset();
    this.createGame();
  }

  public static GAME_WIDTH = 214 + 0;
  public static GAME_HEIGHT = 379 + 0;

  private _container: Container | null = null;
  private _top: Container | null = null;
  private _bottom: Container | null = null;
}
